import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
    Calendar,
    Database,
    History,
    Loader2,
    Search,
    Sparkles
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualList } from '../../hooks/useVirtualList';
import { openSearchModal } from '../../redux/slices/userDocumentSearchSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DBCaseDocumentsSearchThunks, caseDocumentsSearchSelectByQuery } from '../../redux/thunks/DBServiceThunks';
import SmartSearchViewer from './smart-search-viewer';

export default function SearchHistoryView() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
    const [isLoading, setIsLoading] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedSearch, setSelectedSearch] = useState<any>(null);

    // Get search records for this case
    const searchRecords = useAppSelector(state =>
        caseDocumentsSearchSelectByQuery(state.DBCaseDocumentsSearchReducer, { caseId: currentCaseId })
    );

    // Sort by date descending
    const sortedRecords = useMemo(() => {
        return [...searchRecords].sort((a, b) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        );
    }, [searchRecords]);

    useEffect(() => {
        if (currentCaseId) {
            setIsLoading(true);
            dispatch(DBCaseDocumentsSearchThunks.find({ caseId: currentCaseId }))
                .finally(() => setIsLoading(false));
        }
    }, [currentCaseId, dispatch]);

    // Virtual list setup
    const ITEM_HEIGHT = 100;
    const CONTAINER_HEIGHT = 600; // Will be adjusted by flex container
    const {
        scrollElementRef,
        visibleItems,
        totalHeight,
        offsetY,
        handleScroll
    } = useVirtualList(sortedRecords, ITEM_HEIGHT, CONTAINER_HEIGHT);

    const handleOpenViewer = (search: any) => {
        setSelectedSearch(search);
        setViewerOpen(true);
    };

    const handleDeleteSearch = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        dispatch(DBCaseDocumentsSearchThunks.delete({ entity: { _id: id } }));
    };

    if (isLoading && sortedRecords.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
                <Loader2 className="h-10 w-10 text-gold animate-spin mb-4" />
                <p className="text-muted-foreground font-medium">{t('knowledgebase.loadingSearchHistory', 'Loading search history...')}</p>
            </div>
        );
    }

    if (sortedRecords.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center animate-in zoom-in-95 duration-500">
                <div className="p-6 bg-secondary rounded-full mb-6 text-muted-foreground/50">
                    <History className="h-16 w-16" />
                </div>
                <h3 className="text-2xl font-black text-foreground mb-2">{t('knowledgebase.noSearchHistory', 'No Search History Yet')}</h3>
                <p className="text-muted-foreground max-w-sm font-medium">
                    {t('knowledgebase.startSmartSearch', 'Start a Smart Search to see your history here. We preserve all your intelligent analysis for future reference.')}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden p-6 bg-background">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-black text-foreground flex items-center gap-3">
                        <History className="h-7 w-7 text-gold" />
                        {t('knowledgebase.searchHistory', 'Search History')}
                    </h2>
                    <p className="text-muted-foreground font-medium">{t('knowledgebase.storedAnalyses', 'Review your previous searches and AI analyses')}</p>
                </div>
                <Button
                    variant="default"
                    size="sm"
                    onClick={() => dispatch(openSearchModal({ isSearchAll: true }))}
                    className="gap-2 bg-gold hover:bg-gold-bright text-white border-none shadow-md"
                >
                    <Sparkles className="h-4 w-4" />
                    <span className="hidden sm:inline">
                        {t('knowledgebase.smartSearch', 'Smart Search')}
                    </span>
                </Button>
            </div>

            <div
                ref={scrollElementRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto relative rounded-2xl border bg-card shadow-sm"
            >
                <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
                    <div style={{ transform: `translateY(${offsetY}px)` }}>
                        {visibleItems.map((search: any) => (
                            <div
                                key={search._id}
                                onClick={() => handleOpenViewer(search)}
                                className="group px-6 py-4 border-b hover:bg-accent/50 transition-colors cursor-pointer flex items-center gap-6"
                                style={{ height: `${ITEM_HEIGHT}px` }}
                            >
                                <div className="p-3 bg-gold/5 text-gold rounded-2xl group-hover:bg-gold group-hover:text-white transition-all duration-300 shadow-sm shrink-0">
                                    <Search className="h-6 w-6" />
                                </div>

                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                    <p className="text-lg font-black text-foreground truncate mb-1">
                                        {search.queryText}
                                    </p>
                                    <div className="flex items-center gap-1.5 text-muted-foreground font-bold text-xs uppercase tracking-tight">
                                        <Calendar className="h-3.5 w-3.5 text-gold/60" />
                                        {search.createdAt ? format(new Date(search.createdAt), 'MMM d, yyyy • HH:mm') : 'Unknown Date'}
                                    </div>
                                </div>

                                <div className="flex flex-col items-end justify-center gap-2 shrink-0 self-center">
                                    <Badge className={cn(
                                        "uppercase text-[10px] font-black tracking-widest px-2.5 py-1 border-none rounded-lg",
                                        search.status === 'completed' ? "bg-green-100 text-green-700" :
                                            search.status === 'error' ? "bg-red-100 text-red-700" :
                                                "bg-amber-100 text-amber-700 animate-pulse"
                                    )}>
                                        {search.status}
                                    </Badge>
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-secondary text-muted-foreground rounded-md border border-border">
                                        <Database className="h-3 w-3" />
                                        <span className="text-[10px] font-black uppercase tracking-tighter">
                                            {search.results?.length || 0} Matches
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {selectedSearch && (
                <SmartSearchViewer
                    open={viewerOpen}
                    onClose={() => setViewerOpen(false)}
                    searchResults={selectedSearch.results}
                    query={selectedSearch.queryText}
                    title={t('knowledgebase.historicalAnalysis', 'Historical Analysis')}
                />
            )}
        </div>
    );
}
